import { createFileRoute, notFound } from "@tanstack/react-router";

import { BlogPage } from "@/features/blog/components/blog-chrome";
import { useLoadInBrowser } from "@/features/blog/hooks/use-load-in-browser";
import { BlogIndex } from "@/features/blog/modules/blog-index/blog-index";
import { blogListRequest } from "@/features/blog/utils/blog-list-request";
import { AuthorHero } from "@/features/blog/modules/blog-index/components/blog-hero";
import { blogListHead } from "@/features/blog/utils/blog-head";
import { reportApiOutage } from "@/lib/api-outage";
import { m } from "@/paraglide/messages.js";
import { getUrlOrigin } from "@/paraglide/runtime.js";

/** One author: who they are, then everything they signed. An unknown author is a 404. */
export const Route = createFileRoute("/(public)/blog/author/$authorSlug")({
  loader: async ({ params }) => {
    const { data, status } = await blogListRequest({ author: params.authorSlug }).send();
    if (status === 404 || (data && !data.author)) throw notFound();
    // On the server a failed request hands the page to the browser, which asks again.
    if (!data) reportApiOutage();
    if (!data && typeof window !== "undefined") throw new Error(`Could not load this author (status ${status ?? "none"})`);
    return { index: data ?? null, crumb: data?.author?.name ?? m.blog_title() };
  },
  head: ({ loaderData, params }) => {
    const author = loaderData?.index?.author;
    if (!loaderData?.index || !author) return {};
    return blogListHead(loaderData.index, {
      origin: getUrlOrigin(),
      title: `${author.name} · ${m.blog_title()}`,
      description: author.bio || m.blog_author_description({ name: author.name }),
      blogTitle: m.blog_title(),
      path: `/author/${params.authorSlug}`,
    });
  },
  component: BlogAuthorPage,
});

function BlogAuthorPage() {
  const { index } = Route.useLoaderData();
  const { authorSlug } = Route.useParams();
  useLoadInBrowser(index);

  return (
    <BlogPage hero={index?.author ? <AuthorHero author={index.author} /> : null}>
      {index ? <BlogIndex index={index} filter={{ author: authorSlug }} showChips={false} /> : null}
    </BlogPage>
  );
}
